import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, MessageSquare, HelpCircle } from 'lucide-react';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import LanguageMenu from './LanguageMenu';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/yxcl41jr_17fb61916fadfcb65ccf78419aa879e3f44d8d6b4289b01fdfd0f1c53d7537c0.png";

const Navbar = ({ isLoggedIn = false, userType = null, wsConnected = false, wsNotification = null, onQuickTour = null }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    localStorage.removeItem('userName');
    // Hard redirect so AppLayout / WebSocketContext / SubscriptionContext fully reset.
    // (Using navigate() leaves stale isLoggedIn=true in App state.)
    window.location.href = '/';
  };

  const getDashboardPath = () => {
    if (userType === 'contractor') return '/contractor-dashboard';
    if (userType === 'homeowner') return '/homeowner-dashboard';
    if (userType === 'admin') return '/admin';
    return '/worker-dashboard';
  };

  const handleQuickTour = () => {
    if (onQuickTour) {
      onQuickTour();
    } else {
      if (userType === 'contractor') {
        localStorage.removeItem('constructhelp_onboarding_completed');
        navigate('/contractor-dashboard');
      } else if (userType === 'homeowner') {
        navigate('/homeowner-dashboard');
      } else {
        localStorage.removeItem('constructhelp_worker_onboarding_completed');
        navigate('/worker-dashboard');
      }
      window.location.reload();
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 shadow-sm transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={window.Telegram?.WebApp?.initData ? '/tg' : '/'} className="flex items-center" data-testid="navbar-logo-link">
            <img 
              src={LOGO_URL} 
              alt="ConstructHelp" 
              className="h-12 w-auto dark:brightness-110"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Home
            </Link>
            <Link to="/home" data-testid="nav-features-link" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Features
            </Link>
            <Link to="/marketplace" data-testid="nav-marketplace-link" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Marketplace
            </Link>
            {userType !== 'homeowner' && (
              <Link to="/find-workers" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Find Workers
              </Link>
            )}
            {userType !== 'homeowner' && (
              <Link to="/browse-jobs" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Browse Jobs
              </Link>
            )}
            {userType === 'contractor' && (
              <Link to="/ai-scheduler" className="relative text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-testid="nav-ai-scheduler">
                AI Scheduler
                <span className="absolute -top-2.5 -right-7 bg-[#EA580C] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">New</span>
              </Link>
            )}
            {userType === 'homeowner' && (
              <Link to="/get-quotes" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Get Quotes
              </Link>
            )}
            <Link to="/pricing" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              Pricing
            </Link>
            <Link to="/how-it-works" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              How It Works
            </Link>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageMenu />
            <ThemeToggle className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" />
            {isLoggedIn ? (
              <>
                <NotificationBell wsConnected={wsConnected} wsNotification={wsNotification} />
                <Button 
                  onClick={() => navigate('/messages')}
                  variant="outline"
                  className="relative dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  data-testid="nav-messages-btn"
                >
                  <MessageSquare className="h-5 w-5" />
                </Button>
                <Button 
                  onClick={handleQuickTour}
                  variant="outline"
                  className="relative dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  title="Take a quick tour of the platform"
                  data-testid="nav-quick-tour-btn"
                >
                  <HelpCircle className="h-5 w-5" />
                  <span className="hidden lg:inline ml-2">Quick Tour</span>
                </Button>
                <Button 
                  onClick={() => navigate(getDashboardPath())}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-900/30"
                >
                  Dashboard
                </Button>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageMenu compact />
            <ThemeToggle className="text-gray-700 dark:text-gray-300" />
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                Home
              </Link>
              <Link to="/home" data-testid="mobile-nav-features-link" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>
                Features
              </Link>
              <Link to="/marketplace" data-testid="mobile-nav-marketplace-link" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>
                Marketplace
              </Link>
              {userType !== 'homeowner' && (
                <Link to="/find-workers" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                  Find Workers
                </Link>
              )}
              {userType !== 'homeowner' && (
                <Link to="/browse-jobs" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                  Browse Jobs
                </Link>
              )}
              {userType === 'contractor' && (
                <Link to="/ai-scheduler" className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400" data-testid="nav-mobile-ai-scheduler">
                  AI Scheduler
                  <span className="bg-[#EA580C] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">New</span>
                </Link>
              )}
              {userType === 'homeowner' && (
                <Link to="/get-quotes" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                  Get Quotes
                </Link>
              )}
              <Link to="/pricing" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                Pricing
              </Link>
              <Link to="/how-it-works" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                How It Works
              </Link>
              {isLoggedIn ? (
                <>
                  <div className="flex items-center gap-2">
                    <NotificationBell wsConnected={wsConnected} wsNotification={wsNotification} />
                    <span className="text-gray-700 dark:text-gray-300">Notifications</span>
                  </div>
                  <Button 
                    onClick={() => navigate('/messages')}
                    variant="outline"
                    className="w-full dark:border-gray-600 dark:text-gray-300"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Messages
                  </Button>
                  <Button 
                    onClick={handleQuickTour}
                    variant="outline"
                    className="w-full dark:border-gray-600 dark:text-gray-300"
                    data-testid="mobile-quick-tour-btn"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Quick Tour
                  </Button>
                  <Button 
                    onClick={() => navigate(getDashboardPath())}
                    className="w-full"
                  >
                    Dashboard
                  </Button>
                  <Button onClick={handleLogout} variant="outline" className="w-full dark:border-gray-600 dark:text-gray-300">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => navigate('/login')} variant="outline" className="w-full dark:border-gray-600 dark:text-gray-300">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/signup')} className="w-full">
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
